/**
 * Main Layout Component with Header and Sidebar
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, createWsSession, createOrderWsToken } from '../redux/slices/authSlice';
import wsService from '../services/websocket';
import ChatMessenger from './Chat/ChatMessenger';
import AlgoExecutionEngine from './AlgoExecutionEngine';
import api from '../services/api';

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId, userSession, clientId, wsSession, orderToken } = useSelector((state) => state.auth);
  const { isConnected } = useSelector((state) => state.market);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [isAliceBlueConnected, setIsAliceBlueConnected] = useState(false);

  // ── Global algo notification popup ────────────────────────────────────────
  const [algoNotif, setAlgoNotif] = useState({ show: false, type: 'success', msg: '' });
  const algoNotifTimerRef = React.useRef(null);

  const showAlgoNotif = useCallback((type, msg) => {
    setAlgoNotif({ show: true, type, msg });
    if (algoNotifTimerRef.current) clearTimeout(algoNotifTimerRef.current);
    algoNotifTimerRef.current = setTimeout(
      () => setAlgoNotif((p) => ({ ...p, show: false })),
      4500
    );
  }, []);

  useEffect(() => {
    // Same-tab: listen for CustomEvent fired by AlgoExecutionEngine
    const onCustomEvent = (e) => {
      const { type, msg } = e.detail || {};
      if (type && msg) showAlgoNotif(type, msg);
    };
    window.addEventListener('algo_condition_notification', onCustomEvent);

    // Cross-tab: listen for storage changes from other tabs
    const onStorage = (e) => {
      if (e.key === 'algo_condition_notification' && e.newValue) {
        try {
          const { type, msg } = JSON.parse(e.newValue);
          if (type && msg) showAlgoNotif(type, msg);
        } catch (_) {}
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('algo_condition_notification', onCustomEvent);
      window.removeEventListener('storage', onStorage);
    };
  }, [showAlgoNotif]);

  // Check AliceBlue connection status
  useEffect(() => {
    const checkConnection = async () => {
      if (!userSession) {
        setIsAliceBlueConnected(false);
        return;
      }

      try {
        const response = await api.get('/portfolio/positions');
        if (response && response.status === 200) {
          setIsAliceBlueConnected(true);
        } else {
          setIsAliceBlueConnected(false);
        }
      } catch (error) {
        const status = error.response?.status;
        const errorMsg = error.response?.data?.error || error.message || '';

        if (status === 401 || status === 403 ||
            errorMsg.toLowerCase().includes('invalid session') ||
            errorMsg.toLowerCase().includes('session expired') ||
            errorMsg.toLowerCase().includes('unauthorized')) {
          setIsAliceBlueConnected(false);
          handleLogout();
        } else {
          setIsAliceBlueConnected(!!userSession);
        }
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, [userSession]);

  useEffect(() => {
    if (userSession && userId) {
      if (!wsSession) dispatch(createWsSession(userSession));
      if (!orderToken) dispatch(createOrderWsToken(userSession));
      // Establish Socket.IO connection to backend (used for real-time chat)
      wsService.connect(userId, userSession);
    }
    return () => {
      wsService.disconnect();
    };
  }, [userSession, userId, clientId, wsSession, orderToken, dispatch]);

  // Auto-collapse sidebar by default and expand on hover
  useEffect(() => {
    // Set sidebar to collapsed by default
    setSidebarCollapsed(true);
  }, []);

  // Listen for sidebar collapse events from TradingView
  useEffect(() => {
    const handleCollapseSidebar = () => {
      setSidebarCollapsed(true);
    };

    window.addEventListener('collapseSidebar', handleCollapseSidebar);
    return () => {
      window.removeEventListener('collapseSidebar', handleCollapseSidebar);
    };
  }, []);

  // Handle sidebar hover - expands on hover, collapses when mouse leaves
  const handleSidebarMouseEnter = () => {
    setSidebarHovered(true);
  };

  const handleSidebarMouseLeave = () => {
    setSidebarHovered(false);
  };

  // Calculate if sidebar should be expanded (hovered state takes priority)
  const isSidebarExpanded = sidebarHovered || !sidebarCollapsed;

  const handleLogout = () => {
    wsService.disconnect();
    dispatch(logout());
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    // { path: '/market-watch', label: 'Market Watch', icon: '📈' },
    { path: '/market-sentiment', label: 'Market Sentiment', icon: '🧠' },
    { path: '/nse-options-trade', label: 'NSE Options Trade', icon: '📈' },
    { path: '/trading-view', label: 'Quick Trade', icon: '🚀' },
    { path: '/option-chain', label: 'Option Chain', icon: '⛓️' },
    { path: '/algo-trading', label: 'Algo Trading', icon: '🤖' },
    { path: '/algo-conditions', label: 'Algo Conditions', icon: '⚙️' },
    { path: '/master-symbols', label: 'Master Symbols', icon: '🏷️' },
    // { path: '/orders', label: 'Orders', icon: '📋' },
    // { path: '/trades', label: 'Trades', icon: '💹' },
    // { path: '/holdings', label: 'Holdings', icon: '💼' },
    // { path: '/positions', label: 'Positions', icon: '📍' },
    { path: '/funds', label: 'Funds', icon: '💰' },
    { path: '/group-management', label: 'Group Trading', icon: '👥' },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Background algo execution engine — renders null, runs on every page */}
      <AlgoExecutionEngine />

      {/* Global algo notification popup */}
      {algoNotif.show && (
        <div
          className={`fixed top-4 right-4 z-[9999] px-5 py-3 rounded-xl shadow-2xl text-white text-sm font-semibold transition-all
            ${algoNotif.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}
          style={{ maxWidth: '380px', wordBreak: 'break-word' }}
        >
          {algoNotif.msg}
        </div>
      )}
      
      {/* ================= HEADER ================= */}
      <header className="shadow-lg" style={{
        background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 50%, #1e3c72 100%)'
      }}>
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">

            {/* 🍔 HAMBURGER MENU */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="text-white text-2xl mr-4 focus:outline-none hover:text-blue-200 transition"
              title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              ☰
            </button>

            <h1 className="text-2xl font-bold text-white">AliceBlue Trading</h1>

            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              isAliceBlueConnected ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}>
              {isAliceBlueConnected ? '🟢 Connected' : '🔴 Disconnected'}
            </span>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-blue-100 font-medium">User: {userId}</span>
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        
        {/* ================= SIDEBAR ================= */}
        <aside
          className={`relative shadow-md min-h-screen transition-all duration-300 ease-in-out ${
            isSidebarExpanded ? 'w-64' : 'w-16'
          }`}
          style={{
            background: 'linear-gradient(180deg, #1e3c72 0%, #2a5298 50%, #1e3c72 100%)'
          }}
          onMouseEnter={handleSidebarMouseEnter}
          onMouseLeave={handleSidebarMouseLeave}
        >
          <nav className="p-4">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                title={!isSidebarExpanded ? item.label : ''}
                className={({ isActive }) =>
                  `flex items-center ${!isSidebarExpanded ? 'justify-center' : 'space-x-3'}
                   px-4 py-3 rounded-lg mb-2 transition-all duration-300 ease-in-out ${
                     isActive
                       ? 'bg-white bg-opacity-20 text-white shadow-md'
                       : 'text-blue-100 hover:bg-white hover:bg-opacity-10'
                   }`
                }
              >
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <span 
                  className={`font-medium transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap ${
                    isSidebarExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
                  }`}
                >
                  {item.label}
                </span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* ================= MAIN CONTENT ================= */}
        <main className="flex-1 p-6 min-w-0 overflow-x-auto">
          <Outlet />
        </main>
      </div>
      <ChatMessenger />
    </div>
  );
};

export default Layout;
